/* Domain rules for the step 8 registers.
 *
 * These started out inside the screens and moved here for two reasons. They are
 * rules about contracts and DNS rather than anything to do with rendering, and
 * a screen module imports a stylesheet, which makes it unloadable in the test
 * runner — so logic left in a .tsx is logic that cannot be tested.
 *
 * Everything here is a pure function of a record.
 */

import { daysUntil } from './format';
import type { IDevice, IDomain, IPerson, ISupplier } from '../models';

/* ─────────────────────────── Suppliers ─────────────────────────── */

/** Days of notice, parsed out of the free-text column.
 *
 *  It is free text on purpose — '90 days', 'None' and '3 months' are all real
 *  answers, and forcing a number would have meant losing the ones that do not
 *  fit. Anything unparseable is treated as no notice, which errs toward showing
 *  the contract end rather than inventing a deadline earlier than the truth.
 */
export function noticeDays(notice: string | null | undefined): number {
  const text = notice ?? '';
  const months = /(\d+)\s*month/i.exec(text);
  if (months) return Number(months[1]) * 30;
  const days = /(\d+)\s*day/i.exec(text);
  if (days) return Number(days[1]);
  const bare = /^\s*(\d+)\s*$/.exec(text);
  return bare ? Number(bare[1]) : 0;
}

/** The date a decision actually has to be made by.
 *
 *  This is the number the suppliers register exists for. A contract with 90
 *  days' notice that auto-renews has already renewed by the time it shows up in
 *  a 90-day expiry list — the end date is not the deadline, and a screen that
 *  shows only the end date lets the real one pass while looking healthy.
 */
export function decideBy(supplier: Pick<ISupplier, 'end' | 'notice'>): string {
  const days = noticeDays(supplier.notice);
  if (!supplier.end || days === 0) return supplier.end;
  const end = new Date(`${supplier.end}T00:00:00`);
  end.setDate(end.getDate() - days);
  return end.toISOString().slice(0, 10);
}

/** An auto-renewing contract whose notice window has already closed, while the
    contract itself is still running. The worst state in the register: the
    decision got made by nobody making it. */
export function noticeMissed(supplier: Pick<ISupplier, 'end' | 'notice' | 'auto'>): boolean {
  if (!supplier.auto || !supplier.end) return false;
  return daysUntil(decideBy(supplier)) < 0 && daysUntil(supplier.end) >= 0;
}

/* ─────────────────────────── Applications ─────────────────────────── */

/** Absent, 'None' and 'No' all mean the same thing and all appear in real data.
    Anything else is taken as a real answer, because the column is free text —
    'SAML via Entra' is a more useful record than a tick. */
export function isAbsent(value: string | null | undefined): boolean {
  const v = (value ?? '').trim().toLowerCase();
  return v === '' || v === '—' || v === '-' || v === 'none' || v === 'no' || v === 'n/a';
}

/* ─────────────────────────── People ─────────────────────────── */

/* Accounts that are actually a person.
 *
 * Head-counts, the depot switcher's numbers and the estate-by-depot bars all
 * mean "how many people", and Graph hands back shared mailboxes and service
 * accounts in the same list as staff. Counting those makes every one of those
 * numbers wrong in the same direction, quietly.
 *
 * Explicitly 'User' — a blank Kind is *unclassified*, not a person.
 *
 * This used to default blank to 'User', which was right when every row was put
 * there by hand. It stopped being right when the sync started creating a row for
 * every account it finds: shared mailboxes, scanners, service accounts and
 * guests all arrive with no Kind, and counting them as staff would have added
 * eighty people to the head-count overnight.
 *
 * Erring toward not-a-person is the safe direction. An unclassified account
 * missing from the head-count is visibly wrong to anyone who knows the number,
 * and the Unclassified filter leads straight to the fix. An unclassified account
 * silently inflating it is wrong in a way nobody ever notices. */
export function isRealPerson<T extends { kind?: string }>(person: T): boolean {
  return person.kind === 'User';
}

/* Ours, in the sense every app means when it asks for "the staff".
 *
 * `Kind` cannot answer this and was never meant to. It says what an account IS,
 * and by that test Purchase Ledger, a depot workshop login and another
 * company's account are all genuinely User accounts — people sign into them and
 * send mail from them. They are simply not accounts we run the hub for.
 *
 * Those are two different questions, and trying to make one field carry both is
 * what kept producing a list that was nearly right. So this is the second axis:
 * holding a rung of the hub is what makes an account one of ours. It is what
 * opens the hub and the apps, a human sets it during onboarding, and it lapses
 * on its own when a leaver is revoked.
 *
 * Deliberately NOT a tick box beside Kind. That would be a third list for
 * somebody to keep in step, and it would drift the way Kind drifted — silently,
 * and only in the direction of counting people who should not be counted.
 *
 * Status as well as the level, because a disabled account keeps its group
 * membership until somebody revokes it, and a leaver nobody has wound down yet
 * should not still be answering as staff. */
export function isOurs<T extends { hubLevel?: number | null; status?: string }>(
  person: T,
): boolean {
  return (person.hubLevel ?? 0) >= 1 && (person.status ?? 'Active') === 'Active';
}

/** Accounts nobody has classified yet — the sync's discovery worklist. */
export function isUnclassified<T extends { kind?: string }>(person: T): boolean {
  return !(person.kind ?? '').trim();
}

/** Staff, for a head-count. Leavers are not staff and nor is a mailbox. */
export function headcount<T extends { kind?: string; status?: string }>(
  people: readonly T[],
): number {
  return people.filter((p) => isRealPerson(p) && p.status !== 'Leaver').length;
}

/** How an account's MFA reads, and in what colour.
 *
 *  One place, because the table and the drawer each decided separately and
 *  disagreed: the table painted an unrecognised value green and the drawer
 *  showed a dash. An empty MFA column therefore appeared as a green pill with
 *  nothing written in it — "we have no idea" rendered as a security assurance,
 *  which is the wrong direction to be wrong in on this field above all others.
 *
 *  Unknown is muted rather than red on purpose. Nothing populates MFAMethod
 *  today — reading it needs UserAuthenticationMethod.Read.All, which §10 does
 *  not grant — so a blank means "not collected", not "not protected", and
 *  claiming the second would be as false as claiming the first is fine.
 */
export function mfaState(value: string | null | undefined): {
  /** Enabled, Disabled or Not recorded — a list wants an answer, not an
      inventory. The methods themselves belong on the record. */
  label: 'Enabled' | 'Disabled' | 'Not recorded' | 'Not required';
  tone: 'ok' | 'warn' | 'bad' | 'muted';
  /** Each registered method, for the detail panel. */
  methods: string[];
} {
  const v = (value ?? '').trim();
  if (!v || v === '—') return { label: 'Not recorded', tone: 'muted', methods: [] };

  const lower = v.toLowerCase();
  if (lower === 'blocked' || lower.startsWith('not ') || lower === 'none') {
    return { label: 'Disabled', tone: 'bad', methods: [] };
  }

  /* The sync joins methods with ' + ', so this splits back to exactly what it
     wrote. A hand-typed value that does not use that separator is simply one
     method, which is the right reading of it. */
  const methods = v
    .split('+')
    .map((m) => m.trim())
    .filter(Boolean);

  /* Enabled either way, because it is. But MFA that is only a text message is
     the kind that survives a phishing page and a SIM swap, and a list that
     paints it the same green as a security key hides the one difference worth
     acting on. The label stays binary; the colour does the arguing. */
  const weak = (m: string) => /sms|voice|call|phone|email/i.test(m);
  const tone = methods.every(weak) ? 'warn' : 'ok';

  return { label: 'Enabled', tone, methods };
}

/** What MFA should read for an account, given what kind of thing it is.
 *
 *  A service account with nothing registered is correct, not a finding — it
 *  authenticates as an application or it does not sign in at all. Painting that
 *  red put a permanent false alarm next to every one of them, and a security
 *  column that cries wolf is one people stop reading, which costs more than it
 *  ever caught. */
export function mfaFor(
  person: Pick<IPerson, 'mfa' | 'kind'>,
): ReturnType<typeof mfaState> {
  const state = mfaState(person.mfa);
  if (isRealPerson(person) || isUnclassified(person)) return state;
  if (state.label === 'Disabled' || state.label === 'Not recorded') {
    return { label: 'Not required', tone: 'muted', methods: [] };
  }
  return state;
}

/* ─────────────────────────── Licences ─────────────────────────── */

/** The licences that count.
 *
 *  Hidden ones are excluded everywhere a total is computed — seats, idle spend,
 *  renewals, the sidebar count — because a licence you have chosen not to track
 *  should not quietly inflate the number you are judging the estate by. The
 *  licences screen itself still shows them, behind a lens, since hiding
 *  something you can never see again is just deleting it slowly.
 */
export function visibleLicences<T extends { hidden?: boolean }>(skus: readonly T[]): T[] {
  return skus.filter((k) => !k.hidden);
}

/* ─────────────────────────── Domains ─────────────────────────── */

export type DmarcPolicy = 'none' | 'quarantine' | 'reject' | 'missing';

/** The DMARC policy, out of the record.
 *
 *  `p=none` is the one worth separating. It monitors and enforces nothing, so
 *  in any display that reduces DMARC to present-or-absent it reads as
 *  protection while allowing anyone to send as the domain.
 */
export function dmarcPolicy(record: string | null | undefined): DmarcPolicy {
  const m = /\bp\s*=\s*(none|quarantine|reject)\b/i.exec(record ?? '');
  return m ? (m[1].toLowerCase() as DmarcPolicy) : 'missing';
}

/** Whichever of a domain's two clocks runs out first.
 *
 *  They fail differently — a lapsed certificate breaks the site, a lapsed
 *  registration can lose the domain and the mail with it — so both are shown,
 *  and this is only for deciding which one to sort and count on.
 */
export function soonest(domain: Pick<IDomain, 'regEnds' | 'sslEnds'>): string {
  if (!domain.regEnds) return domain.sslEnds;
  if (!domain.sslEnds) return domain.regEnds;
  return daysUntil(domain.regEnds) <= daysUntil(domain.sslEnds) ? domain.regEnds : domain.sslEnds;
}

/* ─────────────────────────── Account lifecycle ─────────────────────────── */

/** Days after leaving before an account is flagged for deletion.
 *
 *  A default, not a policy. Hird's own leaver procedure holds OneDrive for six
 *  months, so plenty of accounts want longer — which is exactly why `keepUntil`
 *  is a stored, editable date rather than this number applied everywhere. */
export const RETENTION_DAYS = 90;

/** No interactive sign-in in this long, on an account still enabled. */
export const DORMANT_DAYS = 90;

function addDays(iso: string, days: number): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** When this account should be gone.
 *
 *  An explicit `keepUntil` always wins — it is the one someone decided, and a
 *  computed default overriding a decision would be the app arguing with its
 *  user. Otherwise it is the default window after they left, and if nothing
 *  recorded when they left there is no clock at all: '' rather than a date
 *  derived from today, which would restart every night and never expire.
 */
export function retentionDue(
  person: Pick<IPerson, 'status' | 'leftOn' | 'keepUntil' | 'kind'>,
): string {
  if (person.keepUntil) return person.keepUntil;
  /* Only a person leaves. A service account or a shared mailbox reads as a
     Leaver because sign-in is blocked in Entra — deliberately, and often for the
     whole of its working life — and statusFor cannot tell the difference from
     accountEnabled alone. Left ungated, every blocked service account started a
     ninety-day countdown to "safe to delete" on an account doing its job. */
  if (!isRealPerson(person)) return '';
  if (person.status !== 'Leaver' || !person.leftOn) return '';
  return addDays(person.leftOn, RETENTION_DAYS);
}

/** A Leaver that is not a leaver: sign-in blocked on something that was never a
 *  person. The blocking is the normal state of the account, not an event. */
export function isBlockedNonPerson(
  person: Pick<IPerson, 'status' | 'kind'>,
): boolean {
  return person.status === 'Leaver' && !isUnclassified(person) && !isRealPerson(person);
}

/** Somebody who has actually left.
 *
 *  Status alone is not enough: it says Leaver for anything with sign-in blocked,
 *  which is the permanent and correct state of most service accounts. Anything
 *  known not to be a person is excluded; anything unclassified is included,
 *  because nobody has said what it is and a worklist should err toward showing
 *  you something rather than hiding it.
 */
export function isLeaver(person: Pick<IPerson, 'status' | 'kind'>): boolean {
  return person.status === 'Leaver' && !isBlockedNonPerson(person);
}

/** How an account's status reads, once you know what kind of thing it is.
 *
 *  Unclassified accounts are left saying Leaver on purpose. Nobody has said what
 *  they are, so claiming the blocking is routine would be a guess — and the
 *  Unclassified filter is where that gets resolved. */
/** Deleted upstream, rather than disabled.
 *
 *  Worth separating from a leaver because the two need opposite things. A
 *  leaver's account still exists, so there is time and there is a process. An
 *  account that has been deleted is already gone: whatever was in its mailbox,
 *  whatever it owned in SharePoint and whatever it was a member of went with it,
 *  and this row is now the only evidence any of it happened.
 *
 *  The sync will not delete the row — §10 rule 3 — so this is the state that
 *  says why it is still here. */
export function isMissingFromSource<T extends { missingSince?: string }>(p: T): boolean {
  return Boolean(p.missingSince);
}

export function statusReading(
  person: Pick<IPerson, 'status' | 'kind'> & { offboardedOn?: string },
): { label: string; tone: 'ok' | 'info' | 'muted' } {
  /* Leaver on something that is not a person is one of two things, and only a stamp
     can tell them apart. Offboarded in Blueprint, then converted to a shared mailbox,
     is a former user, deliberately. Without that stamp it is the sync's old habit of
     reading "sign-in blocked" as "left", which is what a mailbox that was always shared
     got called. Active, set by a person, now stands for either. */
  if (isBlockedNonPerson(person)) {
    return person.offboardedOn
      ? { label: 'Former user', tone: 'muted' }
      : { label: 'Sign-in blocked', tone: 'muted' };
  }
  if (person.status === 'Active') return { label: 'Active', tone: 'ok' };
  if (person.status === 'Starter') return { label: 'Starter', tone: 'info' };
  return { label: 'Leaver', tone: 'muted' };
}

/** An enabled account nobody has used. Never signed in counts as dormant only
 *  once the account has had a chance to be used — a starter created yesterday is
 *  not dormant, they are new, and flagging them would train people to ignore it.
 */
export function isDormant(
  person: Pick<IPerson, 'status' | 'lastSignIn' | 'start' | 'kind'>,
  days = DORMANT_DAYS,
  today = new Date().toISOString().slice(0, 10),
): boolean {
  if (person.status === 'Leaver') return false;
  /* Only real people. A scanner that signs in twice a year is doing its job,
     and a service account with no interactive sign-in ever is the normal case —
     listing them here would bury the handful that matter. */
  if (person.kind !== 'User') return false;

  /* Compared as dates rather than through daysUntil, which measures against the
     real clock — this needs to be answerable for a given day so a test can pin
     one, and so the whole screen agrees on what "today" is. */
  const elapsed = (from: string): boolean => {
    const due = addDays(from, days);
    return due !== '' && due < today;
  };

  const since = person.lastSignIn ?? '';
  if (since) return elapsed(since.slice(0, 10));

  // Never signed in: measure from the start date instead, when there is one.
  return person.start ? elapsed(person.start) : false;
}

export interface OffboardingGap {
  kind: 'licence' | 'group' | 'device' | 'mfa';
  detail: string;
}

/** What is still attached to a leaver.
 *
 *  Every one of these is already visible somewhere — a licence on the licences
 *  screen, a membership on the groups screen, a laptop on the devices screen.
 *  None of it is visible *per leaver*, which is the only arrangement that
 *  answers "did we actually finish offboarding this person".
 */
export function offboardingGaps(
  person: Pick<IPerson, 'status' | 'lic' | 'groups' | 'mfa' | 'kind'>,
  deviceCount = 0,
): OffboardingGap[] {
  /* Same gate as the retention clock, for the same reason. A service account
     holding a licence and sitting in three groups is not unfinished offboarding
     — it is the account working. Counting it put real work and normal operation
     in the same list, which is how a worklist stops being read. */
  if (!isRealPerson(person)) return [];
  if (person.status !== 'Leaver') return [];
  const gaps: OffboardingGap[] = [];

  if (person.lic.length > 0) {
    gaps.push({
      kind: 'licence',
      detail: `${person.lic.length} licence${person.lic.length === 1 ? '' : 's'} still assigned`,
    });
  }
  /* SG-Leavers and the like are put there deliberately by the offboarding
     process, so a group whose whole purpose is holding leavers is not a gap. */
  const held = person.groups.filter((g) => !/leaver/i.test(g));
  if (held.length > 0) {
    gaps.push({ kind: 'group', detail: `still in ${held.length} group${held.length === 1 ? '' : 's'}` });
  }
  if (deviceCount > 0) {
    gaps.push({
      kind: 'device',
      detail: `${deviceCount} device${deviceCount === 1 ? '' : 's'} signed out`,
    });
  }
  /* Registered MFA on a disabled account is not urgent, but it means the
     account was never fully wound down, and it is free to notice. */
  if (person.mfa && !/^(blocked|not registered)$/i.test(person.mfa.trim())) {
    gaps.push({ kind: 'mfa', detail: 'MFA still registered' });
  }

  return gaps;
}

/* ─────────────────────────── Device maintenance ─────────────────────────── */

export interface DeviceSource {
  /** The system, as somebody would say it out loud. */
  name: string;
  /** Which fields on this record it refreshes. */
  owns: string;
}

/** Which systems actually maintain a device record.
 *
 *  Derived from the ids the syncs write, not from RecordSource. RecordSource is
 *  a single hand-typed choice, and it was true when there was one source: it
 *  cannot express a laptop that NinjaOne and Intune both touch, and it goes
 *  stale the moment somebody forgets to change it — at which point the record
 *  says "these fields come from Manual and are refreshed on the nightly sync",
 *  which is nonsense stated confidently.
 *
 *  The ids cannot drift. A row has a NinjaDeviceId because NinjaOne put one
 *  there, and the day it stops being managed the sync stops writing to it.
 *
 *  RecordSource keeps the job it is good at: labelling kit no agent will ever
 *  see — a firewall, a switch, a printer, the one MacBook — where it is the only
 *  thing that can say where the record came from.
 */
/** Enrolled in Intune once, and no longer listed there.
 *
 *  Worth seeing because two of the columns on this record — compliance and
 *  encryption — are Intune's alone and are now frozen at whatever they last
 *  said. The row keeps reporting them, and they stop being true the day this
 *  date appears. */
export function hasLeftIntune<T extends { leftIntune?: string }>(d: T): boolean {
  return Boolean(d.leftIntune);
}

/** Managed by NinjaOne once, and no longer reported there: the agent was removed, or the machine
 *  was replaced under a new name. */
export function hasLeftNinja<T extends { leftNinja?: string }>(d: T): boolean {
  return Boolean(d.leftNinja);
}

/** The machine's current name when it is not the asset tag, else nothing. */
export function renamedTo<T extends { tag: string; hostName?: string }>(d: T): string | undefined {
  const h = (d.hostName ?? '').trim();
  return h && h.toLowerCase() !== d.tag.trim().toLowerCase() ? h : undefined;
}

export function deviceSources(
  device: Pick<IDevice, 'ninjaDeviceId' | 'intuneDeviceId'>,
): DeviceSource[] {
  const sources: DeviceSource[] = [];
  if (device.ninjaDeviceId) {
    sources.push({
      name: 'NinjaOne',
      owns: 'the make, model, serial, CPU, memory, disk, operating system and last-seen date',
    });
  }
  if (device.intuneDeviceId) {
    sources.push({ name: 'Intune', owns: 'compliance and encryption' });
  }
  return sources;
}
